import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase, Listing } from '@/lib/supabase';
import { ListingCard } from '@/components/listings/ListingCard';
import { Loader2, User, Mail, Phone, Clock, CheckCircle2, XCircle, MessageSquare, PlusCircle, Edit, Archive, ArchiveRestore, Trash2, Tag } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

export function ProfilePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchUserListings();
    }
  }, [user]);

  async function fetchUserListings() {
    try {
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setListings(data || []);
    } catch (error) {
      console.error('Error fetching user listings:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleStatusChange = async (listingId: string, newStatus: 'rented' | 'sold' | 'approved') => {
    setActionLoading(listingId);
    try {
        const { error } = await supabase
            .from('listings')
            .update({ status: newStatus })
            .eq('id', listingId);

        if (error) throw error;
        
        // Actualizar UI localmente
        setListings(prev => prev.map(l => l.id === listingId ? { ...l, status: newStatus } : l));
    } catch (error) {
        console.error('Error updating status:', error);
        alert('No se pudo actualizar el estado. Inténtalo de nuevo.');
    } finally {
        setActionLoading(null);
    }
  };

  const handleDelete = async (listingId: string) => {
    if (!confirm('¿Seguro que quieres eliminar este anuncio? No se puede deshacer.')) return;
    setActionLoading(listingId);
    try {
        const { error } = await supabase
            .from('listings')
            .delete()
            .eq('id', listingId);

        if (error) throw error;
        setListings(prev => prev.filter(l => l.id !== listingId));
    } catch (error) {
        console.error('Error deleting listing:', error);
        alert('Error al eliminar el anuncio.');
    } finally {
        setActionLoading(null);
    }
  };

  if (!user) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <p className="text-slate-600 mb-4">Inicia sesión para ver tu perfil</p>
        <Link to="/auth" className="text-blue-600 font-bold hover:underline">Ir al Login</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      {/* Header del Perfil */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 mb-8 flex flex-col md:flex-row items-center gap-6">
        <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center border-4 border-white shadow-md">
          <User className="w-10 h-10 text-slate-400" />
        </div>
        <div className="text-center md:text-left flex-1">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">
            {user.user_metadata?.full_name || 'Usuario'}
          </h1>
          <div className="flex flex-col md:flex-row gap-4 text-slate-500 text-sm">
            <div className="flex items-center gap-1 justify-center md:justify-start">
              <Mail className="w-4 h-4" />
              {user.email}
            </div>
            {user.user_metadata?.phone && (
              <div className="flex items-center gap-1 justify-center md:justify-start">
                <Phone className="w-4 h-4" />
                {user.user_metadata.phone}
              </div>
            )}
          </div>
        </div>
        
        {/* Botones de Acción */}
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <Link 
            to="/profile/messages" 
            className="flex items-center justify-center gap-2 bg-white text-slate-700 border border-slate-200 px-6 py-2.5 rounded-xl font-semibold hover:bg-slate-50 hover:text-blue-600 hover:border-blue-200 transition-all shadow-sm"
          >
            <MessageSquare className="w-5 h-5" />
            Ver Mensajes
          </Link>
          <Link 
            to="/publish" 
            className="flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-blue-700 transition-colors shadow-md shadow-blue-100"
          >
            <PlusCircle className="w-5 h-5" />
            Nuevo Anuncio
          </Link>
        </div>
      </div>

      {/* Mis Anuncios */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-6">Mis Anuncios</h2>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : listings.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {listings.map(listing => (
              <div key={listing.id} className="relative group flex flex-col h-full">
                <div className="relative">
                    <ListingCard listing={listing} />
                    
                    {/* Badge de Estado */}
                    <div className="absolute top-3 right-3 z-10">
                    {listing.status === 'pending' && (
                        <span className="bg-yellow-100 text-yellow-800 text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 shadow-sm border border-yellow-200">
                        <Clock className="w-3 h-3" /> Pendiente
                        </span>
                    )}
                    {listing.status === 'approved' && (
                        <span className="bg-green-100 text-green-800 text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 shadow-sm border border-green-200">
                        <CheckCircle2 className="w-3 h-3" /> Publicado
                        </span>
                    )}
                    {listing.status === 'rejected' && (
                        <span className="bg-red-100 text-red-800 text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 shadow-sm border border-red-200">
                        <XCircle className="w-3 h-3" /> Rechazado
                        </span>
                    )}
                    {listing.status === 'rented' && (
                        <span className="bg-slate-800 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 shadow-sm border border-slate-600">
                        <Archive className="w-3 h-3" /> ALQUILADO
                        </span>
                    )}
                    {listing.status === 'sold' && (
                        <span className="bg-purple-900 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 shadow-sm border border-purple-700">
                        <Tag className="w-3 h-3" /> VENDIDO
                        </span>
                    )}
                    </div>
                </div>

                {/* Controles de Gestión */}
                <div className="mt-3 grid grid-cols-2 gap-2">
                    <button 
                        onClick={() => navigate(`/publish?edit=${listing.id}`)}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-blue-300 hover:text-blue-600 transition-colors"
                    >
                        <Edit className="w-4 h-4" /> Editar
                    </button>
                    
                    {/* BOTÓN DE MARCAR ALQUILADO/VENDIDO */}
                    {(listing.status === 'approved' || listing.status === 'pending') && (
                        <button 
                            onClick={() => handleStatusChange(listing.id, listing.operation_type === 'rent' ? 'rented' : 'sold')}
                            disabled={actionLoading === listing.id}
                            className={`flex items-center justify-center gap-1.5 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                                listing.operation_type === 'rent' 
                                    ? 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-800 hover:text-white'
                                    : 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-900 hover:text-white'
                            }`}
                        >
                            {actionLoading === listing.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                                listing.operation_type === 'rent' ? <Archive className="w-4 h-4" /> : <Tag className="w-4 h-4" />
                            )}
                            {listing.operation_type === 'rent' ? 'Alquilado' : 'Vendido'}
                        </button>
                    )}

                    {(listing.status === 'rented' || listing.status === 'sold') && (
                        <button 
                            onClick={() => handleStatusChange(listing.id, 'approved')}
                            disabled={actionLoading === listing.id}
                            className="flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg text-sm font-medium text-blue-700 hover:bg-blue-100 transition-colors"
                        >
                            {actionLoading === listing.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArchiveRestore className="w-4 h-4" />}
                            Reactivar
                        </button>
                    )}

                    {/* Botón de Borrar (para pendientes o rechazados) */}
                    {(listing.status === 'pending' || listing.status === 'rejected') && (
                        <button 
                            onClick={() => handleDelete(listing.id)}
                            disabled={actionLoading === listing.id}
                            className="col-span-2 flex items-center justify-center gap-1.5 px-3 py-2 bg-white border border-red-200 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                        >
                            {actionLoading === listing.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            Eliminar Anuncio
                        </button>
                    )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-slate-50 rounded-2xl border border-slate-200 border-dashed">
            <p className="text-slate-500 mb-4">Aún no tienes anuncios publicados.</p>
            <Link to="/publish" className="text-blue-600 font-medium hover:underline">
              ¡Publica tu primer trastero gratis!
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
